/* SystemBannerRenderer.js */

"use strict";
const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");
const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;
const TextControl = uiCore.Control.Textfield.InputText;
const TextArea = uiCore.Control.Textfield.Textarea;
const SingleComboBox = uiCore.Control.OptionSelection.Single.ComboBox;

const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;

const PreviewComponent = require("./veil/previewbanner/PreviewBannerComponent");

class SystemBannerRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("banner-gl");
        this._rootElem.component = model;

        this._headerElem = document.createElement("h1");
        this._headerElem.innerText = "Banner";
        this._headerElem.setAttribute("title", "Installed versions of Gluware modules");
        this._mainContainer = document.createElement("container-main");

        this._bannerContent = document.createDocumentFragment();

        this._bannerDefaults = this._model.defaultProperties.banner;
        this._bannerData = undefined;

        this._formElem = document.createElement("div");
        this._formElem.classList.add("banner-form");

        this._bannerContent.appendChild(this._formElem);

        this._fieldsSection = document.createElement("div");
        this._fieldsSection.classList.add("form-fields");

        this._isValid = false;
        this._errors = [];

        this._enableBannerCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable System Banner",
            title: "Display a banner on sign in",
            disabled: false
        });

        this._bannerHTMLTextControl = new TextArea({
            darkTheme: true
        });

        this._bannerAcceptTextControl = new TextControl({
            darkTheme: true,
            value: this._bannerDefaults.acceptButtonLabel,
            label: "Acceptance Button Text"
        });

        this._bannerRefuseTextControl = new TextControl({
            darkTheme: true,
            value: this._bannerDefaults.refusalButtonLabel,
            label: "Refusal Button Text"
        });

        this._acceptButton = new ButtonControl({
            active: false,
            class: "update-banner-settings",
            darkTheme: true,
            label: "Save",
            disabled: true
        });

        this._cancelButton = new ButtonControl({
            class: "cancel",
            darkTheme: true,
            label: "Cancel",
            disabled: true
        });

        this._previewButton = new ButtonControl({
            class: "preview-banner",
            label: "Preview",
            title: "Preview the configured banner",
            darkTheme: true,
            disabled: true
        });

        this._resetAcceptance = new ButtonControl({
            class: "reset-acceptance",
            label: "Reset User Acknowledgments",
            title: "Clear acceptance so all users must accept the banner again",
            darkTheme: true,
            disabled: true
        });

        this._displayControl = new SingleComboBox({
            darkTheme: true,
            options: [
                {value: {id: "ONCE", name: "ONCE"}, label: "Only on first sign-in per user"},
                {value: {id: "ALWAYS", name: "ALWAYS"}, label: "On every sign-in"}
            ]
        });

        this._actionsContainer = document.createElement("actions-container");
        this._actionsContainer.classList.add("action-container");


        this._registerListeners();
    }

    get rootElement() {
        return this.getRootElement();
    }
    get model() {
        return this.getModel();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() { 
        this._acceptButton.on("UserClick", this._submitAction = this._submitAction.bind(this));
        this._cancelButton.on("UserClick", this._cancelAction = this._cancelAction.bind(this));
        this._bannerAcceptTextControl.on("UserValue", this._onAcceptanceTextChange = this._onAcceptanceTextChange.bind(this));
        this._bannerHTMLTextControl.on("UserValue", this._onHTMLTextChange = this._onHTMLTextChange.bind(this));
        this._bannerRefuseTextControl.on("UserValue", this._onRefusalTextChange = this._onRefusalTextChange.bind(this));
        this._enableBannerCheckbox.on("UserValue", this._onEnableBannerChange = this._onEnableBannerChange.bind(this));
        this._displayControl.on("UserValue", this._onDisplayControlChange = this._onDisplayControlChange.bind(this));
        this._previewButton.on("UserClick", this._previewAction = this._previewAction.bind(this));
        this._resetAcceptance.on("UserClick", this._onResetAcceptance = this._onResetAcceptance.bind(this));
    }

    _unregisterListeners() {
        this._acceptButton.removeListener("UserClick", this._submitAction);
        this._cancelButton.removeListener("UserClick", this._cancelAction);
        this._bannerRefuseTextControl.removeListener("UserValue", this._onRefusalTextChange);
        this._bannerAcceptTextControl.removeListener("UserValue", this._onAcceptanceTextChange);
        this._bannerHTMLTextControl.removeListener("UserValue", this._onHTMLTextChange);
        this._enableBannerCheckbox.removeListener("UserValue", this._onEnableBannerChange);
        this._previewButton.removeListener("UserClick", this._previewAction);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    getRootElement() {
        return this._rootElem;
    }
    getModel() {
        return this._model;
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (this._model) {
            return this._model
                .whenModelIsReady()
                .then(() => {
                    this._bannerData = this._model._bannerData;
                    this._isValid = this._validateData();
                    if (this._rootElem) {
                        this._rootElem.innerHTML = "";
                        this._rootElem.appendChild(this._headerElem);
                        this._rootElem.appendChild(this._mainContainer);
                        this._showControls();
                        this._mainContainer.appendChild(this._bannerContent);

                        this._showForm();
                        this._updateData(this._bannerData);
                        this._updateActionState(true);
                        if(this._model._myUserInfo.authorizedActions.indexOf("configWrite") < 0) {                            
                            this._disableAllFields();
                        }
                    }
                })
                .catch(err => {
                    this._model._logger.warn(err);
                });
        }
    }

    _updateData(data) {
        this._enableBannerCheckbox.setValue(data.enable);
        this._bannerHTMLTextControl.setValue(data.text);
        this._bannerRefuseTextControl.setValue(data.refusalButtonLabel);
        this._bannerAcceptTextControl.setValue(data.acceptButtonLabel);
        this._displayControl.setValue({id: data.displayMode, name: data.displayMode});
        this._enableDisableFields(data.enable);
    }

    shutdown() {
        this._unregisterListeners();
        const controls = [
            this._enableBannerCheckbox,
            this._bannerHTMLTextControl,
            this._bannerAcceptTextControl,
            this._bannerRefuseTextControl,
            this._displayControl,
            this._acceptButton,
            this._cancelButton,
            this._previewButton,
            this._resetAcceptance
        ];

        return Promise.all(_.invokeMap(controls, "shutdown")).then(() => {
            this._rootElem.remove();
            this._headerElem = undefined;
            this._rootElem = undefined;
            this._enableBannerCheckbox = undefined;
            this._bannerHTMLTextControl = undefined;
            this._bannerAcceptTextControl = undefined;
            this._bannerRefuseTextControl = undefined;
            this._displayControl = undefined;
            this._acceptButton = undefined;
            this._cancelButton = undefined;
            this._previewButton = undefined;
            this._bannerData = undefined;
            this._bannerDefaults = undefined;
            this._formElem = undefined;
            this._actionsContainer = undefined;
            this.errors = undefined;
            this._resetAcceptance = undefined;
        });
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _showControls() {
        this._actionsContainer.appendChild(this._resetAcceptance.renderer.getRootElement());
        this._actionsContainer.appendChild(this._cancelButton.renderer.getRootElement());
        this._actionsContainer.appendChild(this._previewButton.renderer.getRootElement());
        this._actionsContainer.appendChild(this._acceptButton.renderer.getRootElement());
        this._bannerContent.appendChild(this._actionsContainer);
    }

    _showForm() {
        this._formElem.appendChild(this._enableBannerCheckbox.renderer._rootElem);
        this._renderFormPair("Banner Text","Banner text in HTML format", this._bannerHTMLTextControl);
        this._renderFormPair("Acceptance Button Text", "Text to display on the Accept button", this._bannerAcceptTextControl);
        this._renderFormPair("Refusal Button Text", "Text to display on the Refuse button", this._bannerRefuseTextControl);
        this._renderFormPair("Display", "Display once per user or every time each user signs in", this._displayControl);
        this._formElem.appendChild(this._fieldsSection);
    }

    _disableActions({acceptBtn, cancelBtn, previewBtn} = {acceptBtn:true, cancelBtn:true, previewBtn:true}) {
        this._acceptButton.setDisabled(acceptBtn);
        this._cancelButton.setDisabled(cancelBtn);
        this._previewButton.setDisabled(previewBtn);
    }

    _renderFormPair(labelName, tooltip, component, parentEl) {
        const parent = parentEl ? parentEl : this._fieldsSection;
        let labelElem = document.createElement("label");
        labelElem.innerText = labelName;
        labelElem.setAttribute("title", tooltip);
        let cmpntElem = component.renderer.getRootElement();
        parent.appendChild(labelElem);
        parent.appendChild(cmpntElem);
    }

    _createSection(content, options = {}) {
        const section = document.createElement("section");
        section.className = options.className || "";
        section.classList.toggle("bordered", !!options.bordered);

        if (options.title) {
            const title = document.createElement("h2");
            title.innerText = options.title;
            section.appendChild(title);
        }
        section.appendChild(content);
        return section;
    }
    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onAcceptanceTextChange(val) {
        this._model._updatedBannerData.acceptButtonLabel = val;
        this._setDirtyState();
        this._updateActionState();
    }

    _onRefusalTextChange(val) {
        this._model._updatedBannerData.refusalButtonLabel = val;
        this._setDirtyState();
        this._updateActionState();
    }

    _onHTMLTextChange(val) {
        this._model._updatedBannerData.text = val;
        this._setDirtyState();
        this._updateActionState();
    }

    _onResetAcceptance() {
        this._model._modalUserFeedback.displayConfirmation("Are you sure you want to reset and force all users to acknowledge the banner again?",{
            title: "Reset User Acknowledgments",
            confirmDetails: {
                text: "Reset",
                type: "destructive"
            },
            rejectDetails: {
                text: "Cancel",
                type: "text"
            }
        })
        .then(() => {
            const newData = Object.assign({},this._model._bannerData );
            newData.updatedOn = new Date().toISOString();
            this._model._systemConfigEntity.setBanner(newData);
            return this._model._systemConfigEntity.update();
        })
        .then(() => {
            this._model._updatedBannerData.updatedOn = this._model._systemConfigEntity.banner.updatedOn;
            this._model._bannerData.updatedOn = this._model._systemConfigEntity.banner.updatedOn;
        })
        .catch(() => {
            this._model._logger.warn("System Banner :: User Acknowledgments not reset");
        });
    }

    _onEnableBannerChange(bool) {
        this._enableDisableFields(bool);
        this._model._updatedBannerData.enable = bool;
        this._setDirtyState();
        this._updateActionState();
        // this._model._saveBanner(false);
    }

    _enableDisableFields(enable) {
        if(enable) {
            this._fieldsSection.classList.remove("disabled");
            this._bannerHTMLTextControl.setDisabled(false);
            this._bannerRefuseTextControl.setDisabled(false);
            this._bannerAcceptTextControl.setDisabled(false);
            this._displayControl.setDisabled(false);
            this._resetAcceptance.setDisabled(false);
        } else {
            this._fieldsSection.classList.add("disabled");
            this._bannerHTMLTextControl.setDisabled(true);
            this._bannerRefuseTextControl.setDisabled(true);
            this._bannerAcceptTextControl.setDisabled(true);
            this._displayControl.setDisabled(true);
            this._resetAcceptance.setDisabled(true);
        }
    }

    _disableAllFields() {
        this._enableBannerCheckbox.setDisabled(true);
        this._enableDisableFields(false);
        if(!this._isValid) {
            this._disableActions({acceptBtn:true, cancelBtn:true, previewBtn:true});
        } else {
            this._disableActions({acceptBtn: true, cancelBtn: true, previewBtn: false});
        }
    }

    _onDisplayControlChange(val) {
        this._model._updatedBannerData.displayMode = val.id;
        this._setDirtyState();
        this._updateActionState();
    }

    _setDirtyState() {
        this._model._isDirty = !_.isEqual(this._bannerData, this._model._updatedBannerData);
        // this._isValid = this._validateData();
    }

    _updateActionState(firstLoad = false) {
        if(!this._isValid && firstLoad) {
            this._disableActions({acceptBtn:true, cancelBtn:true, previewBtn:true});
            return;
        }
        if(this._model._isDirty) {
            this._disableActions({acceptBtn:false, cancelBtn:false, previewBtn:false});
        } else {
            this._disableActions({acceptBtn:true, cancelBtn:true, previewBtn:false});
        }
    }


    _submitAction() {
        if(this._validateData()) {
            this._model._saveBanner(true).then(() => {
                this._updateActionState();
            });
        } else {
            this._model._displayError(this._errors);
        }
    }

    _cancelAction() {
        this.model._cancelBannerChanges().then(data => {
            this._updateData(data);
            this._updateActionState();
        });
    }

    _previewAction() {
        if(this._validateData()) {
            const previewCmpt = new PreviewComponent(this._model._updatedBannerData);
            this._model._modalUserFeedback.displayCustom(previewCmpt);
        } else {
            this._model._displayError(this._errors);
        }
    }
    _onOrganizationChange(/*org*/) {
    }

    // _onDataUpdated() {
    //     this._setPristineProperties();
    // }
    _onOrgDataChanged() {
        //this._fileServersEnabledCheckBox.setVisible(!this.model.activeOrgIsRoot);
    }

    /* Control change events : End */
    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _validateData() {
        const validHTMLTags = ["h1","h2","h3","h4","h5","h6","p","span","a","img","i","b", "strong","em","ul","ol","li"];
        let isValid = false;
        const data = this._model._updatedBannerData;
        this._errors = [];

        if(!data.text) {
            this._errors.push({ key: "Banner Text Invalid", err: "Banner Text is required."});
            this._bannerHTMLTextControl.renderer._rootElem.classList.add("error");
            isValid = false;
        } else {
            isValid = true;
            this._bannerHTMLTextControl.renderer._rootElem.classList.remove("error");
        }

        if(!data.refusalButtonLabel) {
            this._errors.push({ key: "Label Invalid", err: "Refusal button label is required."});
            isValid = false;
        } else {
            isValid = isValid && true;
        }

        if(!data.displayMode) {
            isValid = false;
            this._errors.push({ key: "Display mode Invalid", err: "Display mode value is required."});
        } else {
            isValid = isValid && true;
        }
        
        if(!data.acceptButtonLabel) {
            isValid = false;
            this._errors.push({ key: "Label Invalid", err: "Acceptance button label is required."});
        } else {
            isValid = isValid && true;
        }

        if(!isValid) {
            return isValid;
        }

        let tagsInText = data.text.match(/<[^/> ]*( |\/>)?(?!\/)/gm);
        tagsInText = 
            tagsInText 
            ? tagsInText.map( tag => {
            return tag.replace(/<|( |\/>)/gm, '');
            })
            : false;

        if(!tagsInText) {
            return isValid;
        }

        const allowedTagsStr = validHTMLTags.join(";") + ";";
        const htmlIsValid = tagsInText.every((tag) => {
            return allowedTagsStr.search(`${tag};`) >= 0;
        });
        if(!htmlIsValid) {
            this._errors.push({
                key: "Invalid Tags",
                err: `Only the following HTML tags are allowed in the banner text: ${allowedTagsStr}.`
            });
        }

        return isValid && htmlIsValid;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = SystemBannerRenderer;
